import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Eye, PenLine, Copy, Check, Share2, Link as LinkIcon, Loader2 } from "lucide-react";
import API_BASE_URL from "../services/apiClient";

const TripInviteModal = ({ isOpen, onClose, travelKeyToken }) => {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // Responsive state for animations
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      // Delay reset so the user doesn't see it disappear while animating out
      setTimeout(() => {
        setLink(null);
        setSelectedRole(null);
        setLoading(false);
        setCopied(false);
      }, 300);
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const createInvite = async (role = "viewer") => {
    try {
      setSelectedRole(role);
      setLoading(true);
      setLink(null); // Reset link when switching roles

      const token = sessionStorage.getItem("token") || localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/trips/${travelKeyToken}/invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role }),
      });

      const data = await res.json();

      if (res.ok) {
        // Mock a slight delay for better perceived value / UX
        setTimeout(() => {
          setLink(data.inviteLink);
          setLoading(false);
        }, 600);
      } else {
        alert(data.message || "Invite failed");
        setLoading(false);
      }
    } catch (err) {
      alert("Server error");
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] items-center justify-center ">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          />

          {/* Modal Container */}
          
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={isMobile ? { y: "100%" } : { opacity: 0, scale: 0.95, y: 10 }}
            animate={isMobile ? { y: 0 } : { opacity: 1, scale: 1, y: 0 }}
            exit={isMobile ? { y: "100%" } : { opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="relative w-full max-w-md bg-[#F7F6F4] sm:rounded-[32px] rounded-t-[32px] shadow-2xl p-6 sm:p-8 flex flex-col"
          >
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                  Invite to Trip
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Share access and plan together
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 -mr-2 -mt-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200/50 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Role Selection */}
            <div className="space-y-3 mb-6">
              {/* Viewer Card */}
              <button
                onClick={() => createInvite("viewer")}
                className={`w-full group relative flex items-center p-4 rounded-2xl transition-all duration-200 border active:scale-[0.98] ${
                  selectedRole === "viewer"
                    ? "border-[#5b6f00] bg-[#5b6f00]/5 shadow-sm"
                    : "border-black/5 bg-white hover:border-[#5b6f00]/30 hover:shadow-sm"
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 transition-colors ${
                    selectedRole === "viewer"
                      ? "bg-[#5b6f00]/10 text-[#5b6f00]"
                      : "bg-gray-100 text-gray-500 group-hover:bg-[#5b6f00]/10 group-hover:text-[#5b6f00]"
                  }`}
                >
                  <Eye className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">Viewer</p>
                    {selectedRole === "viewer" && (
                      <span className="text-[11px] uppercase tracking-wider font-bold text-[#5b6f00]">
                        Selected
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Can view itinerary and trip details
                  </p>
                </div>
                {selectedRole === "viewer" && (
                  <div className="absolute inset-0 rounded-2xl ring-1 ring-[#5b6f00] ring-inset" />
                )}
              </button>

              {/* Editor Card */}
              <button
                onClick={() => createInvite("editor")}
                className={`w-full group relative flex items-center p-4 rounded-2xl transition-all duration-200 border active:scale-[0.98] ${
                  selectedRole === "editor"
                    ? "border-[#5b6f00] bg-[#5b6f00]/5 shadow-sm"
                    : "border-black/5 bg-white hover:border-[#5b6f00]/30 hover:shadow-sm"
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 transition-colors ${
                    selectedRole === "editor"
                      ? "bg-[#5b6f00]/10 text-[#5b6f00]"
                      : "bg-gray-100 text-gray-500 group-hover:bg-[#5b6f00]/10 group-hover:text-[#5b6f00]"
                  }`}
                >
                  <PenLine className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">Editor</p>
                    {selectedRole === "editor" && (
                      <span className="text-[11px] uppercase tracking-wider font-bold text-[#5b6f00]">
                        Selected
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Can edit and manage the trip
                  </p>
                </div>
                {selectedRole === "editor" && (
                  <div className="absolute inset-0 rounded-2xl ring-1 ring-[#5b6f00] ring-inset" />
                )}
              </button>
            </div>

            {/* Loading State */}
            <AnimatePresence>
              {loading && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex flex-col items-center justify-center py-6 overflow-hidden"
                >
                  <Loader2 className="w-6 h-6 animate-spin text-[#5b6f00] mb-3" />
                  <p className="text-sm font-medium text-gray-600">
                    Generating invite link...
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Invite Link Section */}
            <AnimatePresence>
              {link && !loading && (
                <motion.div
                  initial={{ opacity: 0, y: 10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  className="overflow-hidden"
                >
                  <div className="pt-2 border-t border-black/5">
                    <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Invite Link
                    </label>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex-1 flex items-center bg-white border border-black/5 rounded-xl px-3 py-2.5 shadow-sm overflow-hidden">
                        <LinkIcon className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <input
                          value={link}
                          readOnly
                          className="flex-1 bg-transparent text-sm text-gray-700 outline-none w-full"
                        />
                      </div>
                      <button
                        onClick={copyLink}
                        className={`relative overflow-hidden flex items-center justify-center h-[42px] min-w-[96px] px-4 rounded-xl text-sm font-semibold transition-all duration-200 ${
                          copied
                            ? "bg-green-500 text-white shadow-md shadow-green-500/20"
                            : "bg-[#5b6f00] text-white hover:bg-[#4c5c00] shadow-sm hover:shadow-md active:scale-95"
                        }`}
                      >
                        <AnimatePresence mode="wait">
                          {copied ? (
                            <motion.div
                              key="check"
                              initial={{ opacity: 0, scale: 0.5 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.5 }}
                              transition={{ duration: 0.15 }}
                              className="flex items-center gap-1.5"
                            >
                              <Check className="w-4 h-4" /> Copied
                            </motion.div>
                          ) : (
                            <motion.div
                              key="copy"
                              initial={{ opacity: 0, scale: 0.5 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.5 }}
                              transition={{ duration: 0.15 }}
                              className="flex items-center gap-1.5"
                            >
                              <Copy className="w-4 h-4" /> Copy
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </button>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          window.open(
                            `https://wa.me/?text=${encodeURIComponent(
                              `Join my trip on Expeditio!\n${link}`
                            )}`
                          )
                        }
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold bg-[#25D366]/10 text-[#128C7E] hover:bg-[#25D366]/20 transition-colors active:scale-[0.98]"
                      >
                        <Share2 className="w-4 h-4" />
                        WhatsApp
                      </button>
                      <button
                        onClick={() => window.open(link, "_blank")}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold bg-white border border-black/5 text-gray-700 hover:bg-gray-50 transition-colors shadow-sm active:scale-[0.98]"
                      >
                        <Eye className="w-4 h-4" />
                        Open Link
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TripInviteModal;